import React, { useEffect, useRef, useState } from 'react'
import { hu } from 'date-fns/locale'
import { DateRangePicker, START_DATE, END_DATE } from 'react-nice-dates'
import '../css/StyledDateRangePicker.css'

const timeOf = (date) => date ? date.getTime() : null

const sameRange = (a, b) => timeOf(a.from) === timeOf(b.from) && timeOf(a.to) === timeOf(b.to)

// The parent is notified once the user stops editing, i.e. the calendar has no focus. This runs on
// every unfocused render rather than only on the moment focus is lost: when clicking away, the
// document click and the input blur may arrive in either order, and a notification bound to that
// transition would miss the blur when it lands last. Hence settle is idempotent.
function SettleWatcher({focus, onSettle}) {
    useEffect(() => {
        if (!focus) {
            onSettle()
        }
    })

    return null
}

export default function StyledDateRangePicker(props) {
    const [draft, setDraft] = useState({from: props.fromDate, to: props.toDate})

    // A single click may trigger both library callbacks, so the pending value is also kept in a ref:
    // the second call then sees the fresh value instead of the one captured by the render.
    const draftRef = useRef(draft)

    // The range handed to the parent most recently, guarding against repeated notifications.
    const notifiedRef = useRef({from: props.fromDate, to: props.toDate})

    const fromTime = timeOf(props.fromDate)
    const toTime = timeOf(props.toDate)

    // After a start date is picked, react-nice-dates always moves focus to the end date
    // (handleSelectDate, START_DATE branch) even when that pick completed the range, leaving the
    // calendar open and forcing the user to pick an end date again. Focus is internal state of
    // DateRangePicker and cannot be set from outside, so bumping the key remounts it: focus resets,
    // the calendar closes and settle runs.
    const [pickerKey, setPickerKey] = useState(0)

    const setBoth = (next) => {
        draftRef.current = next
        setDraft(next)
    }

    // Only the pick that supplies the last missing endpoint closes the calendar, which leaves the
    // usual "start first, then end" order untouched: there the end date branch closes it.
    const handleStartDateChange = (date) => {
        const completesRange = draftRef.current.from == null && date != null && draftRef.current.to != null
        setBoth({from: date, to: draftRef.current.to})

        if (completesRange) {
            setPickerKey(key => key + 1)
        }
    }

    // A date change coming from outside (period selector, session storage) overrides the pending value.
    useEffect(() => {
        notifiedRef.current = {from: props.fromDate, to: props.toDate}
        setBoth({from: props.fromDate, to: props.toDate})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromTime, toTime])

    // react-nice-dates nulls the start date on an invalid range, and while typing it reports every
    // parseable intermediate value. Those are editing states rather than results, so they do not
    // leave this component until the edit is finished.
    const settle = () => {
        const next = draftRef.current
        const applied = {from: props.fromDate, to: props.toDate}

        // An incomplete range cannot filter, so revert to the applied one and keep the inputs from
        // showing something the listed data does not reflect.
        if (!props.allowEmpty && (next.from == null || next.to == null)) {
            if (!sameRange(next, applied)) {
                setBoth(applied)
            }
            return
        }

        // notifiedRef also catches repeats when the parent does not echo back exactly what it got.
        if (sameRange(next, applied) || sameRange(next, notifiedRef.current)) {
            return
        }

        notifiedRef.current = next
        props.onRangeChange(next.from, next.to)
    }

    return (
        <section style={{position: "relative"}}>
            <DateRangePicker
                key={pickerKey}
                startDate={draft.from}
                endDate={draft.to}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={(date) => setBoth({from: draftRef.current.from, to: date})}
                maximumLength={props.maximumLength}
                format="yyyy-MM-dd"
                locale={hu}
            >
                {({startDateInputProps, endDateInputProps, focus}) => (
                    <div className='date-range'>
                        <input
                            className={focus === START_DATE ? 'input-focused' : 'input'}
                            {...startDateInputProps}
                            placeholder='Kezdő dátum'
                        />
                        <span className='date-range_arrow'/>
                        <input
                            className={focus === END_DATE ? 'input-focused' : 'input'}
                            {...endDateInputProps}
                            placeholder='Vég dátum'
                        />
                        <SettleWatcher focus={focus} onSettle={settle}/>
                    </div>
                )}
            </DateRangePicker>
        </section>
    );
}
